import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

function IconFirst({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M11 6L5 12l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M18 6L12 12l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconPrev({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M15 6L9 12l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconNext({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconLast({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M6 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconMistakePrev({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M12 9v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M12 17h.01" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M10.5 4.5L3.8 18a1.2 1.2 0 001.1 1.8h14.2a1.2 1.2 0 001.1-1.8L13.5 4.5a1.2 1.2 0 00-2.1 0z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M8 12H5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconMistakeNext({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M12 9v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      <path d="M12 17h.01" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
      <path d="M10.5 4.5L3.8 18a1.2 1.2 0 001.1 1.8h14.2a1.2 1.2 0 001.1-1.8L13.5 4.5a1.2 1.2 0 00-2.1 0z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M16 12h3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Under-board ply scrubber — start / prev / next / end (+ optional mistake jumps).
 */
export default function MovePlyNav({
  totalPlies = 0,
  activePly = 0,
  viewingPast = false,
  canScrub = false,
  currentSan = '',
  showMistakes = false,
  onStart,
  onPrev,
  onNext,
  onEnd,
  onPrevMistake,
  onNextMistake,
  className = '',
}) {
  const { t } = useTranslation();
  const maxPly = Math.max(0, Number(totalPlies) || 0);
  const ply = Math.max(0, Math.min(maxPly, Number(activePly) || 0));
  const progress = maxPly <= 0 ? 0 : (ply / maxPly) * 100;
  const atStart = ply <= 0;
  const atEnd = !viewingPast || ply >= maxPly;

  const headline = useMemo(() => {
    if (maxPly <= 0) {
      return t('playing.noMovesYet', { defaultValue: 'No moves yet' });
    }
    if (ply <= 0) {
      return t('playing.plyNavStart', { defaultValue: 'Starting position' });
    }
    const moveNo = Math.ceil(ply / 2);
    const isWhite = ply % 2 === 1;
    const sanPart = currentSan
      ? (isWhite ? `${moveNo}. ${currentSan}` : `${moveNo}… ${currentSan}`)
      : t('playing.plyNavPly', { ply, defaultValue: `Ply ${ply}` });
    if (!viewingPast) {
      return t('playing.plyNavLive', {
        label: sanPart,
        defaultValue: `Live · ${sanPart}`,
      });
    }
    return sanPart;
  }, [maxPly, ply, viewingPast, currentSan, t]);

  if (!canScrub || maxPly <= 0) {
    return null;
  }

  return (
    <div
      className={`playing-ply-scrub ${viewingPast ? 'playing-ply-scrub--review' : 'playing-ply-scrub--live'} ${className}`.trim()}
      role="group"
      aria-label={t('playing.moves', { defaultValue: 'Moves' })}
    >
      <div className="playing-ply-scrub__meta">
        <div className="playing-ply-scrub__meta-text">
          <span className="playing-ply-scrub__kicker">
            {viewingPast
              ? t('playing.reviewing', { defaultValue: 'Reviewing' })
              : t('playing.moves', { defaultValue: 'Moves' })}
          </span>
          <p className="playing-ply-scrub__headline" title={headline}>
            {headline}
          </p>
        </div>
        <div className="playing-ply-scrub__count" aria-hidden={false}>
          <span className="playing-ply-scrub__count-now tabular-nums">{ply}</span>
          <span className="playing-ply-scrub__count-sep">/</span>
          <span className="playing-ply-scrub__count-total tabular-nums">{maxPly}</span>
        </div>
      </div>

      <div className="playing-ply-scrub__track" aria-hidden>
        <div className="playing-ply-scrub__track-fill" style={{ width: `${progress}%` }} />
        <div className="playing-ply-scrub__track-thumb" style={{ left: `${progress}%` }} />
      </div>

      <div className="playing-ply-scrub__controls">
        <button
          type="button"
          className="playing-ply-scrub__btn"
          aria-label={t('playing.plyStart', { defaultValue: 'First position' })}
          title={t('playing.plyStart', { defaultValue: 'First position' })}
          onClick={onStart}
          disabled={atStart}
        >
          <IconFirst className="playing-ply-scrub__icon" />
        </button>
        <button
          type="button"
          className="playing-ply-scrub__btn"
          aria-label={t('playing.plyPrev', { defaultValue: 'Previous move' })}
          title={t('playing.plyPrev', { defaultValue: 'Previous move' })}
          onClick={onPrev}
          disabled={atStart}
        >
          <IconPrev className="playing-ply-scrub__icon" />
        </button>
        <button
          type="button"
          className="playing-ply-scrub__btn playing-ply-scrub__btn--primary"
          aria-label={t('playing.plyNext', { defaultValue: 'Next move' })}
          title={t('playing.plyNext', { defaultValue: 'Next move' })}
          onClick={onNext}
          disabled={atEnd}
        >
          <IconNext className="playing-ply-scrub__icon" />
        </button>
        <button
          type="button"
          className="playing-ply-scrub__btn playing-ply-scrub__btn--primary"
          aria-label={t('playing.plyEnd', { defaultValue: 'Latest position' })}
          title={t('playing.plyEnd', { defaultValue: 'Latest position' })}
          onClick={onEnd}
          disabled={atEnd}
        >
          <IconLast className="playing-ply-scrub__icon" />
        </button>

        {showMistakes && (
          <div className="playing-ply-scrub__mistakes" role="group" aria-label={t('playing.mistakes', { defaultValue: 'Mistakes' })}>
            <button
              type="button"
              className="playing-ply-scrub__btn playing-ply-scrub__btn--warn"
              aria-label={t('playing.prevMistake', { defaultValue: 'Previous mistake' })}
              title={t('playing.prevMistake', { defaultValue: 'Previous mistake' })}
              onClick={onPrevMistake}
            >
              <IconMistakePrev className="playing-ply-scrub__icon" />
            </button>
            <button
              type="button"
              className="playing-ply-scrub__btn playing-ply-scrub__btn--warn"
              aria-label={t('playing.nextMistake', { defaultValue: 'Next mistake' })}
              title={t('playing.nextMistake', { defaultValue: 'Next mistake' })}
              onClick={onNextMistake}
            >
              <IconMistakeNext className="playing-ply-scrub__icon" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
